class UrlMatcher {
  constructor(url) {
    this.parts = this.parse(url)
  }

  parse(url) {
    if (!url || typeof url !== 'string') {
      return { pathname: '', search: '', hash: '' }
    }

    try {
      const urlObj = url.startsWith('http') ? new URL(url) : new URL(url, location.href)
      return {
        pathname: urlObj.pathname,
        search: urlObj.search,
        hash: urlObj.hash
      }
    } catch {
      const [pathAndSearch, hash = ''] = url.split('#')
      const [pathname, search = ''] = pathAndSearch.split('?')
      return {
        pathname,
        search: search ? `?${search}` : '',
        hash: hash ? `#${hash}` : ''
      }
    }
  }

  matchPathname(other) {
    const otherPathname = other instanceof UrlMatcher ? other.parts.pathname : other
    return this.parts.pathname === otherPathname
  }
}

// Returns true if both search strings are equal (two empty strings also match).
function searchMatches(menuMatcher, currentMatcher) {
  const m = menuMatcher.parts.search
  const c = currentMatcher.parts.search
  if (!m && !c) return true
  if (!m || !c) return false
  return m === c
}

// Returns true if the menu hash and current hash are exactly equal (including both empty).
function hashExactMatch(menuMatcher, currentMatcher) {
  const m = menuMatcher.parts.hash
  const c = currentMatcher.parts.hash
  if (!m && !c) return true
  return m === c
}

// Returns true if the current hash starts with the menu hash as a sub-path
// (separated by '/' or '?', e.g. #tab-general/section or #tab-general?id=1).
function hashPrefixMatch(menuMatcher, currentMatcher) {
  const menuPath = menuMatcher.parts.hash.replace(/^#/, '')
  const curPath = currentMatcher.parts.hash.replace(/^#/, '')
  if (!menuPath || !curPath) return false
  return curPath.startsWith(`${menuPath}/`) || curPath.startsWith(`${menuPath}?`)
}

// Among multiple candidates, prefer exact hash match, then longer (more specific) hash.
function pickBestByHash(candidates, currentMatcher) {
  if (candidates.length === 0) return null
  if (candidates.length === 1) return candidates[0].item

  const curPath = currentMatcher.parts.hash.replace(/^#/, '')

  // Pre-compute hash paths to avoid repeated replace() calls inside the comparator.
  const mapped = candidates.map((c) => ({
    item: c.item,
    hashPath: c.matcher.parts.hash.replace(/^#/, '')
  }))

  return mapped.sort((a, b) => {
    const exactA = a.hashPath === curPath
    const exactB = b.hashPath === curPath
    if (exactA !== exactB) return exactA ? -1 : 1
    return b.hashPath.length - a.hashPath.length
  })[0].item
}

// ─── URL normalization ────────────────────────────────────────────────────────

const HAS_TAB_QUERY_RE = /(?:^|[?&])tab=/

/**
 * Normalizes the current URL into a canonical form for menu matching.
 *
 * /setting?tab=general           → /setting#tab-general
 * /setting#tab-general?tab=general → /setting#tab-general  (malformed: ?tab= inside hash)
 */
function resolveMenuMatchTarget() {
  const { pathname, search, hash } = location

  if (pathname === '/account/setting' || pathname === '/profile/setting') {
    if (search && HAS_TAB_QUERY_RE.test(search)) {
      const params = new URLSearchParams(search)
      const tab = params.get('tab')
      if (tab) {
        params.delete('tab')
        const rest = params.toString()
        const base = `${pathname}${rest ? `?${rest}` : ''}`
        const tabSeg = `tab-${tab}`
        const frag = hash.replace(/^#/, '').replace(/\?.*$/, '')
        const h = frag ? `#${tabSeg}/${frag}` : `#${tabSeg}`
        return `${base}${h}`
      }
    } else if (!hash && window.microsDatas?.useSimplifiedPortal) {
      const tab = window.setData?.pageType || ''
      if (tab) {
        return `${pathname}#tab-${tab}`
      }
    }
  }

  // Strip query-like params embedded inside the hash fragment
  const normalizedHash = hash.includes('?') ? hash.replace(/\?.*$/, '') : hash
  return `${pathname}${search}${normalizedHash}`
}

// ─── Special menu match handlers ─────────────────────────────────────────────

const MATCH_HANDLER = {
  'sidemenu-mail-calendar-management': (item) =>
    item.items?.find((menuItem) => menuItem.url && location.pathname.startsWith(menuItem.url)),
  new_ai_studio_column_container: (item) =>
    item.items?.find((menuItem) => menuItem.url && location.pathname.startsWith(menuItem.url)),
  phone_number_management_column_container: (item) =>
    item.items?.find((menuItem) => {
      if (!menuItem.url) return false
      const urlPathname = menuItem.url.split('#')[0]
      return location.pathname.startsWith(urlPathname)
    })
}

// ─── Core matching ────────────────────────────────────────────────────────────

// Extracts pathname from a URL string cheaply without full URL parsing.
// Handles both relative paths (/foo/bar#hash) and absolute URLs (https://x.com/foo/bar).
function extractPathnameQuick(url) {
  if (url.startsWith('http')) {
    try {
      return new URL(url).pathname
    } catch {
      /* fall through */
    }
  }
  return url.split(/[?#]/)[0] || '/'
}

function collectPathnameMatches(menuList, currentMatcher) {
  const results = []
  const targetPathname = currentMatcher.parts.pathname

  const walk = (items, parent = null) => {
    if (!Array.isArray(items)) return

    for (const item of items) {
      const handler = MATCH_HANDLER[item.id]
      if (handler && Array.isArray(item.items)) {
        const match = handler(item)
        if (match) {
          results.push({ item: { ...match, parent: item }, matcher: new UrlMatcher(match.url) })
          continue
        }
      }

      if (item.url && item.target !== '_blank') {
        // Quick pathname check before paying the cost of full URL parsing.
        if (extractPathnameQuick(item.url) !== targetPathname) {
          if (Array.isArray(item.items)) walk(item.items, item)
          continue
        }
        const menuMatcher = new UrlMatcher(item.url)
        if (menuMatcher.matchPathname(currentMatcher)) {
          results.push({ item: parent ? { ...item, parent } : item, matcher: menuMatcher })
        }
      }

      if (Array.isArray(item.items)) walk(item.items, item)
    }
  }

  walk(menuList)
  return results
}

function matchMenuAgainstTarget(menuList, targetPath) {
  if (targetPath === '/') return null

  const currentMatcher = new UrlMatcher(targetPath)

  const pathnameMatches = collectPathnameMatches(menuList, currentMatcher)

  if (pathnameMatches.length === 0) return null
  if (pathnameMatches.length === 1) return pathnameMatches[0].item

  const searchFiltered = pathnameMatches.filter(({ matcher }) =>
    searchMatches(matcher, currentMatcher)
  )
  const candidates = searchFiltered.length > 0 ? searchFiltered : pathnameMatches

  if (candidates.length === 1) return candidates[0].item

  const exactHashMatches = candidates.filter(({ matcher }) =>
    hashExactMatch(matcher, currentMatcher)
  )
  if (exactHashMatches.length > 0) return pickBestByHash(exactHashMatches, currentMatcher)

  const prefixHashMatches = candidates.filter(({ matcher }) =>
    hashPrefixMatch(matcher, currentMatcher)
  )
  if (prefixHashMatches.length > 0) return pickBestByHash(prefixHashMatches, currentMatcher)

  return pickBestByHash(candidates, currentMatcher)
}

function getMenuItemByURL(menuList) {
  return matchMenuAgainstTarget(menuList, resolveMenuMatchTarget())
}

export function getMenuItemByProps(array, targetValue, propName, parent = null) {
  if (!targetValue || !propName || !Array.isArray(array)) return null

  for (const item of array) {
    const isMatch =
      propName === 'url' && item[propName]
        ? item[propName].includes(targetValue)
        : item[propName] === targetValue

    if (isMatch) return { item, parent }

    if (Array.isArray(item.items)) {
      const found = getMenuItemByProps(item.items, targetValue, propName, item)
      if (found) return found
    }
  }

  return null
}

export const findActiveMenu = (sideMenusArray, sideMenuActiveKey) => {
  const pathMatch = getMenuItemByURL(sideMenusArray)
  if (pathMatch) {
    return {
      type: 'path',
      target: { item: pathMatch, parent: pathMatch.parent }
    }
  }

  const keyMatch = getMenuItemByProps(sideMenusArray, sideMenuActiveKey, 'key')
  if (keyMatch?.item) {
    return { type: 'key', target: keyMatch }
  }

  const reserveKeyMatch = getMenuItemByProps(sideMenusArray, sideMenuActiveKey, 'reserveKey')
  if (reserveKeyMatch?.item) {
    return { type: 'reserveKey', target: reserveKeyMatch }
  }

  return null
}
